import { ChangeEvent, useEffect, useState } from 'react'
import { useSPD } from '../../../views/SPDLayout/SPD/SPD.context'
import { useGetTrayItemByBarcode } from 'lib/apollo/hooks'
import { BetterIdResult } from 'common/types'
import { useLocation } from 'react-router-dom'
import { TrayItemProductDetails } from 'lib/services/api/product-service/receiveItems/types'
import { SelectTrayIDModalLogicProps } from './SelectTrayIDModal.types'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'

const useSelectTrayIDModal = ({
  shouldBeFacilityOwned,
  isAssigningFromSurgery = false,
  handleAssignDigitalTray,
  isAssigningTray,
}: SelectTrayIDModalLogicProps) => {
  const location = useLocation()

  const { setTrayData, setTrayScrews, setTrayPlates, setCustomImplants } =
    useSPD()
  const { loading, error, trayItem, getTrayItem } = useGetTrayItemByBarcode()

  const [isComplete, setIsComplete] = useState<boolean>(false)
  const [scannedCode, setScannedCode] = useState<string>('')
  const [productData, setProductData] = useState<BetterIdResult[]>([])

  const trayDetails: TrayItemProductDetails | undefined = trayItem
    ? (JSON.parse(trayItem.productDetails as string) as TrayItemProductDetails)
    : undefined

  const trayCategory =
    trayDetails?.trayCategory === 'consigned'
      ? 'Consigned'
      : trayDetails?.trayCategory === 'loaner'
      ? 'Loaner'
      : trayDetails?.trayCategory === 'facility'
      ? 'Facility Owned Tray'
      : 'Tray Category Unassigned'

  const shouldBeDisabled = isAssigningFromSurgery
    ? trayCategory !== 'Facility Owned Tray' &&
      !trayDetails?.statusDetails?.isApproved
    : shouldBeFacilityOwned
    ? trayCategory !== 'Facility Owned Tray'
    : trayCategory === 'Facility Owned Tray'

  const handleGetTrayItem = (barcode: string) => {
    getTrayItem({ variables: { barcode } })
  }

  useEffect(() => {
    if (scannedCode.length > 0) {
      handleGetTrayItem(scannedCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedCode])

  const handleTrayIDChange = (e: ChangeEvent<HTMLInputElement>) => {
    const scannedCodeValue = e.target.value

    setScannedCode(scannedCodeValue)
  }

  const handleConfirmTray = () => {
    if (scannedCode.length > 0) {
      if (!trayItem) {
        throw new Error('No tray data found.')
      }

      const subTrayDetails = extractSubTrayJSONData(trayItem.subItems?.[0])
      setTrayScrews(subTrayDetails?.screws || [])
      setTrayPlates(subTrayDetails?.plates || [])
      setCustomImplants(subTrayDetails?.customImplants || [])
      setTrayData({
        trayID: scannedCode,
        trayItem,
      })

      if (isAssigningFromSurgery && trayDetails) {
        handleAssignDigitalTray?.(trayItem, trayDetails)
      }
    } else {
      alert('You have not entered or captured a tray ID.')
      throw new Error('You have not entered or captured a tray ID.')
    }
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const encodedTrayData = queryParams.get('trayData')

    if (encodedTrayData) {
      try {
        const decodedTrayData = JSON.parse(atob(encodedTrayData))
        const subTrayDetails = extractSubTrayJSONData(
          decodedTrayData.trayItem.subItems?.[0]
        )
        setTrayScrews(subTrayDetails?.screws || [])
        setTrayPlates(subTrayDetails?.plates || [])
        setTrayData(decodedTrayData)
      } catch (error) {
        console.error('Failed to decode tray data:', error)
      }
    }
  }, [location.search, setTrayData])

  return {
    handleTrayIDChange,
    loading: loading || isAssigningTray,
    error,
    isComplete,
    setIsComplete,
    productData,
    setProductData,
    scannedCode,
    setScannedCode,
    trayItem,
    handleConfirmTray,
    trayDetails,
    trayCategory,
    shouldBeDisabled,
    shouldBeFacilityOwned,
  }
}

export default useSelectTrayIDModal
